import { Dispatch, SetStateAction, useState } from 'react'
import { useAblyTypedPubSub } from '../use-ably'
import { HallMetadata } from '@db8/shared'
import { useAxios } from '@/contexts/AxiosProvider'
import { isEqual } from 'lodash'
import { useUser } from '@clerk/clerk-react'

export const useHallMetadataPubSub = ({
  hallMetadata,
  setHallMetadata,
  hallId,
}: {
  hallMetadata: HallMetadata
  setHallMetadata: Dispatch<SetStateAction<HallMetadata>>
  hallId?: string
}) => {
  const axios = useAxios()
  const [lastSender, setLastSender] = useState<string | null>(null)
  const { user: clerkUser } = useUser()

  const { publish } = useAblyTypedPubSub<Partial<HallMetadata>>(
    'HALL_METADATA',
    ({ data, sender }) => {
      if (!data || typeof data !== 'object') return
      setHallMetadata((prev) => {
        const next = { ...prev, ...data }
        if ('activeTts' in data) {
          const incoming = (data as any).activeTts
          const prevNonEmpty = !!prev?.activeTts
          const incomingEmpty = !incoming
          if (incomingEmpty && prevNonEmpty) {
            next.activeTts = prev.activeTts
          }
        }
        return next
      })
      setLastSender(sender)
    },
    hallId ? { url: `/api/hall/${hallId}/metadata`, instant: true } : undefined,
  )

  const isPatchChanged = (patch: Partial<HallMetadata>) =>
    Object.entries(patch).some(([k, v]) => !isEqual(hallMetadata[k as keyof HallMetadata], v))

  const publishHallMetadata = async (patch: Partial<HallMetadata>) => {
    if (!isPatchChanged(patch)) return
    // Always publish locally/over pubsub so clients update immediately
    publish(patch)
    if (clerkUser) setLastSender(clerkUser.id)

    // Filter out transient, frontend-only fields that are not persisted server-side
    const transientKeys = new Set<keyof Partial<HallMetadata>>([
      // playback-only client state
      'ttsRate' as any,
      'ttsTime' as any,
    ])

    const serverPatch = Object.fromEntries(
      Object.entries(patch).filter(([k]) => !transientKeys.has(k as any)),
    ) as Partial<HallMetadata>

    // Skip server call if nothing persistable remains
    if (Object.keys(serverPatch).length === 0) return

    await axios.patch(`/api/hall/${hallId}/metadata`, serverPatch)
  }

  return { publishHallMetadata, hallMetadata, lastSender }
}
